import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  CreatePrescriptionRequestT,
  MedicationDecisionRequestT,
  MedicationDecisionResponseT,
  MedicationDecisionT,
  PriorAuthCommandRejectionT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import type { RxRequest } from '@/entities/rx';
import { rxKeys, useCreatePrescriptionMutation } from '@/entities/rx';
import { submitMedicationDecision } from '@/entities/prior-auth';
import { extractApiErrorMessage } from '@/shared';
import { useSubmissionOrchestrator } from '@/shared/lib/submission';

export type RxSubmissionStatus =
  | 'idle'
  | 'creating'
  | 'sending'
  | 'confirmation-pending'
  | 'success'
  | 'failed-create'
  | 'failed-send';

/**
 * Final disposition of a `send` step, mirroring the backend's per-row outcome
 * buckets. Surfacing this on the orchestrator state lets downstream UI render
 * the truth ("Prior auth sent, case #123") without having to look up the
 * Rx-wizard PA preview, which can disagree with the Rx-time gate.
 */
export type RxSendDisposition =
  | { kind: 'sent-to-pharmacy' }
  | { kind: 'prior-auth-initiated'; caseNumber: string | null }
  | { kind: 'confirmation-pending' }
  | { kind: 'excluded' }
  | { kind: 'divert-failed'; error: string };

export type RxSendResult =
  | { kind: 'completed'; disposition: RxSendDisposition }
  | {
      kind: 'rejected';
      rejectionCode: PriorAuthCommandRejectionT | null;
      message: string;
    };

export function resolveMedicationDecisionResponse(
  response: MedicationDecisionResponseT,
  decision: MedicationDecisionT
): RxSendResult {
  if (response.outcome === 'REJECTED') {
    return {
      kind: 'rejected',
      rejectionCode: response.rejectionCode ?? null,
      message:
        response.rejectionDetail ??
        response.rejectionCode ??
        'The prescription decision was rejected.',
    };
  }

  const disposition: RxSendDisposition =
    response.outcome === 'CONFIRMATION_PENDING'
      ? { kind: 'confirmation-pending' }
      : decision === 'START_PA'
        ? {
            kind: 'prior-auth-initiated',
            caseNumber: response.currentCase?.dosespotCase?.caseNumber ?? null,
          }
        : { kind: 'sent-to-pharmacy' };

  return { kind: 'completed', disposition };
}

export interface RxSubmissionState {
  status: RxSubmissionStatus;
  dosespotPrescriptionId: string | null;
  prescriptionRecordId: string | null;
  errorMessage: string | null;
  /**
   * Populated once a send step resolves. `null` until the first send attempt
   * completes (or while still `idle` / `creating`).
   */
  sendDisposition: RxSendDisposition | null;
}

const INITIAL_STATE: RxSubmissionState = {
  status: 'idle',
  dosespotPrescriptionId: null,
  prescriptionRecordId: null,
  errorMessage: null,
  sendDisposition: null,
};
const SUBMITTING_STATUSES: ReadonlySet<RxSubmissionStatus> = new Set(['creating', 'sending']);

export interface CreateRxSubmissionArgs {
  userId: string;
  rxRequest: RxRequest;
  sourceProposedActionExternalId?: string | null;
  diagnosis?: {
    diagnosisId?: number;
    diagnosisCode?: string;
    diagnosisDescription?: string;
  };
}

export type SourceProposedActionRequestFields = {
  sourceProposedActionExternalId?: string | null;
};

export type RxMedicationDecisionOptions = Omit<MedicationDecisionRequestT, 'prescriptionRecordId'>;

export function buildMedicationDecisionRequest(
  prescriptionRecordId: string,
  options: RxMedicationDecisionOptions
): MedicationDecisionRequestT {
  return {
    prescriptionRecordId,
    ...options,
  };
}

export interface RxSubmissionOrchestrator {
  /** Current submission state */
  state: RxSubmissionState;

  /** Whether the draft is locked (prescription created, awaiting send or already sent) */
  isDraftLocked: boolean;

  /** Whether a submission operation is in progress */
  isSubmitting: boolean;

  /** Create a prescription in DoseSpot */
  create: (args: CreateRxSubmissionArgs) => Promise<{
    dosespotPrescriptionId: string;
    prescriptionRecordId: string;
  } | null>;

  /**
   * Send a created prescription to the pharmacy.
   *
   * Resolves to the backend result, or ``null`` when the request fails.
   * Returned directly (rather than forcing callers
   * to re-read ``state.sendDisposition``) so post-send callbacks don't
   * race against React's state update — the state version is one render
   * stale inside the very callback that awaited this promise.
   */
  send: (
    args: {
      userId: string;
      dosespotPrescriptionId: string;
      prescriptionRecordId: string;
    } & RxMedicationDecisionOptions
  ) => Promise<RxSendResult | null>;

  /** Create and send in one call - returns the dosespotPrescriptionId on success */
  createAndSend: (
    args: CreateRxSubmissionArgs
  ) => Promise<{ dosespotPrescriptionId: string; prescriptionRecordId: string } | null>;

  /**
   * Retry the send step. Same return contract as {@link send}.
   */
  retrySend: (userId: string, options: RxMedicationDecisionOptions) => Promise<RxSendResult | null>;

  /** Reset to initial idle state */
  reset: () => void;
}

export function useRxSubmissionOrchestrator(): RxSubmissionOrchestrator {
  const queryClient = useQueryClient();
  const createPrescriptionMutation = useCreatePrescriptionMutation();
  const { state, isDraftLocked, isSubmitting, runPhase, patchState, reset } =
    useSubmissionOrchestrator<RxSubmissionState>({
      initialState: INITIAL_STATE,
      feature: 'rx-form',
      submittingStatuses: SUBMITTING_STATUSES,
      isDraftLocked: (current) => current.dosespotPrescriptionId !== null,
    });

  const invalidateQueries = useCallback(
    async (userId: string, dosespotPrescriptionId: string) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: rxKeys.patientPrescriptions(userId) }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.prescription(userId, dosespotPrescriptionId),
        }),
      ]);
    },
    [queryClient]
  );

  const create = useCallback(
    async (
      args: CreateRxSubmissionArgs
    ): Promise<{
      dosespotPrescriptionId: string;
      prescriptionRecordId: string;
    } | null> => {
      return runPhase({
        pendingStatus: 'creating',
        execute: async () => {
          const request: CreatePrescriptionRequestT & SourceProposedActionRequestFields = {
            prescriptionData: args.rxRequest,
            sourceProposedActionExternalId: args.sourceProposedActionExternalId ?? null,
            ...(args.diagnosis
              ? {
                  diagnosis: {
                    // ZEKKA NOTE: This showed up after regenerating the SDK. Previously, the two objects were apparently compatible.
                    // I'm not sure if this is a bug in the SDK, in greenfield, or in Cortex.
                    // After checking Greenfield, these types look right.
                    diagnosis_id: args.diagnosis.diagnosisId,
                    diagnosis_code: args.diagnosis.diagnosisCode,
                    diagnosis_description: args.diagnosis.diagnosisDescription,
                  },
                }
              : {}),
          };

          const created = await createPrescriptionMutation.mutateAsync({
            userId: args.userId,
            data: request,
          });

          const id = created.prescription.PrescriptionId;
          if (id === null || id === undefined) {
            throw new Error('Create prescription response missing PrescriptionId.');
          }

          const dosespotPrescriptionId = String(id);
          await invalidateQueries(args.userId, dosespotPrescriptionId);
          return { dosespotPrescriptionId, prescriptionRecordId: created.prescriptionRecordId };
        },
        getSuccessPatch: (result) => ({
          dosespotPrescriptionId: result.dosespotPrescriptionId,
          prescriptionRecordId: result.prescriptionRecordId,
        }),
        getError: (error) => ({
          status: 'failed-create',
          message: extractApiErrorMessage(error, 'Could not create prescription.'),
          operation: 'create',
          logMessage: 'rx submission create failed',
        }),
      });
    },
    [createPrescriptionMutation, invalidateQueries, runPhase]
  );

  const send = useCallback(
    async (
      args: {
        userId: string;
        dosespotPrescriptionId: string;
        prescriptionRecordId: string;
      } & RxMedicationDecisionOptions
    ): Promise<RxSendResult | null> => {
      return runPhase<RxSendResult>({
        pendingStatus: 'sending',
        execute: async () => {
          const { userId, dosespotPrescriptionId, prescriptionRecordId, ...decision } = args;
          const response = await submitMedicationDecision(
            buildMedicationDecisionRequest(prescriptionRecordId, decision)
          );
          await invalidateQueries(userId, dosespotPrescriptionId);
          return resolveMedicationDecisionResponse(response, args.decision);
        },
        getSuccessPatch: (result) =>
          result.kind === 'rejected'
            ? {
                status: 'failed-send',
                errorMessage: result.message,
                sendDisposition: null,
              }
            : {
                status:
                  result.disposition.kind === 'confirmation-pending'
                    ? 'confirmation-pending'
                    : 'success',
                sendDisposition: result.disposition,
              },
        getError: (error) => ({
          status: 'failed-send',
          message: extractApiErrorMessage(error, 'Could not send prescription.'),
          operation: 'send',
          logMessage: 'rx submission send failed',
        }),
      });
    },
    [invalidateQueries, runPhase]
  );

  const createAndSend = useCallback(
    async (
      args: CreateRxSubmissionArgs
    ): Promise<{
      dosespotPrescriptionId: string;
      prescriptionRecordId: string;
    } | null> => {
      const created = await create(args);
      if (!created) {
        return null;
      }

      const result = await send({
        userId: args.userId,
        dosespotPrescriptionId: created.dosespotPrescriptionId,
        prescriptionRecordId: created.prescriptionRecordId,
        decision: 'SEND_NO_PA',
      });

      return result?.kind === 'completed' ? created : null;
    },
    [create, send]
  );

  const retrySend = useCallback(
    async (userId: string, options: RxMedicationDecisionOptions): Promise<RxSendResult | null> => {
      const id = state.dosespotPrescriptionId;
      const prescriptionRecordId = state.prescriptionRecordId;
      if (!id || !prescriptionRecordId) {
        patchState({
          status: 'failed-send',
          errorMessage: 'Missing created prescription.',
        });
        return null;
      }

      return send({
        userId,
        dosespotPrescriptionId: id,
        prescriptionRecordId,
        ...options,
      });
    },
    [patchState, send, state.dosespotPrescriptionId, state.prescriptionRecordId]
  );

  return {
    state,
    isDraftLocked,
    isSubmitting,
    create,
    send,
    createAndSend,
    retrySend,
    reset,
  };
}
